import { computed, watch, type Ref, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';

/**
 * Composable for validating review token
 * Checks if the token is valid, expired, already used, or cancelled
 */
export function useValidateReviewToken(token: Ref<string | undefined> | ComputedRef<string | undefined>) {
  const api = useApi();
  const toaster = useToaster();

  const shouldValidate = computed(() => !!token.value);

  const { data, isLoading, error } = useQuery({
    queryKey: ['review', 'validate-token', token],
    queryFn: async () => {
      if (!token.value) {
        throw new Error('Token is required');
      }
      
      return await api.public.reviewsV2.validateToken({
        params: {
          token: token.value
        }
      });
    },
    enabled: shouldValidate,
    retry: 1,
    staleTime: 0, // Always revalidate
    gcTime: 0, // Don't cache
  });

  const isValid = computed(() => {
    return data.value?.is_valid === true;
  });

  const validationReason = computed(() => {
    return data.value?.reason;
  });

  const expertData = computed(() => {
    return data.value?.expert;
  });

  const projectData = computed(() => {
    return data.value?.project;
  });

  const message = computed(() => {
    return data.value?.message;
  });

  const clientExists = computed(() => {
    return data.value?.client_exists;
  });

  watch(error, (newError: any) => {
    if (newError) {
        if (newError.response?.status === 404 || newError.response?.status === 400) {
            return;
        }
      const errorMessage = 
        newError?.response?.data?.message || 
        newError?.response?.data?.error ||
        newError?.message ||
        'Failed to validate review token. Please check the link and try again.';
      
      toaster.create({
        title: 'Invalid Review Link',
        description: errorMessage,
        type: 'error',
      });
    }
  });

  watch(data, (newData) => {
    if (newData && !newData.is_valid && newData.reason) {
      let errorTitle = 'Invalid Review Link';
      let errorDescription = '';

      switch (newData.reason) {
        case 'not_found':
          errorDescription = 'This review link is invalid. Please contact the expert who sent you this link.';
          break;
        case 'expired':
          errorDescription = 'This review link has expired. Please contact the expert to send you a new link.';
          break;
        case 'already_used':
          errorDescription = 'You have already submitted a review using this link. Thank you for your feedback!';
          break;
        case 'cancelled':
          errorDescription = 'This review request has been cancelled by the expert.';
          break;
        default:
          errorDescription = 'This review link is no longer valid.';
      }

      toaster.create({
        title: errorTitle,
        description: errorDescription,
        type: 'error',
      });
    }
  });

  return {
    data,
    isLoading,
    error,
    isValid,
    validationReason,
    expertData,
    projectData,
    message,
    clientExists,
  };
}

